import { Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { ContactForm } from "../components/ContactForm";
import { CONTACT, waHref } from "../i18n/translations";

const VALID_INTERESTS = ["general", "valuation", "buy", "sell", "rent", "rent_mid", "manage", "concierge"];

export default function Contact() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const requested = params.get("interest");
  const defaultInterest = VALID_INTERESTS.includes(requested) ? requested : "general";
  const waLink = waHref(t("contact.whatsappText"));

  return (
    <div data-testid="contact-page" className="pt-36 md:pt-44 pb-24 md:pb-32">
      <Seo title={t("meta.contactTitle")} description={t("contact.subtitle")} />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-accent text-xs uppercase tracking-[0.25em] text-primary">{t("contact.overline")}</p>
            <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground">{t("contact.title")}</h1>
            <p className="mt-6 font-body font-light text-lg text-foreground/70 leading-relaxed">{t("contact.subtitle")}</p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 border-t border-border/60 pt-10">
              <h3 className="font-accent text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">{t("contact.reachTitle")}</h3>
              <ul className="flex flex-col gap-6">
                <li>
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-direct-email" className="flex items-center gap-4 group">
                    <span className="w-11 h-11 flex items-center justify-center border border-primary/40 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300"><Mail size={17} /></span>
                    <span className="font-body text-foreground/85 group-hover:text-primary transition-colors">{CONTACT.email}</span>
                  </a>
                </li>
                <li>
                  <a href={waLink} target="_blank" rel="nofollow noopener noreferrer" data-testid="contact-direct-whatsapp" className="flex items-center gap-4 group">
                    <span className="w-11 h-11 flex items-center justify-center border border-primary/40 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300"><MessageCircle size={17} /></span>
                    <span className="font-body text-foreground/85 group-hover:text-primary transition-colors">{CONTACT.whatsapp}</span>
                  </a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-11 h-11 flex items-center justify-center border border-primary/40 text-primary"><MapPin size={17} /></span>
                  <span className="font-body text-foreground/85">{t("contact.location")}</span>
                </li>
                <li className="flex items-center gap-4">
                  <span className="w-11 h-11 flex items-center justify-center border border-primary/40 text-primary"><MapPin size={17} /></span>
                  <span className="font-body text-foreground/85">{t("contact.locationMonaco")}</span>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-11 h-11 flex-shrink-0 flex items-center justify-center border border-primary/40 text-primary"><Clock size={17} /></span>
                  <div className="flex flex-col gap-1">
                    <span className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("contact.hoursLabel")}</span>
                    <span data-testid="contact-hours-weekdays" className="font-body text-foreground/85">{t("contact.hoursWeekdays")}</span>
                    <span data-testid="contact-hours-saturday" className="font-body text-foreground/85">{t("contact.hoursSaturday")}</span>
                    <span data-testid="contact-hours-sunday" className="font-body text-foreground/60">{t("contact.hoursSunday")}</span>
                  </div>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-10 border-t border-border/60 pt-10">
              <h3 className="font-accent text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">{t("contact.mapTitle")}</h3>
              <div data-testid="contact-map-embed" className="relative w-full aspect-[4/3] overflow-hidden border border-border/60 bg-secondary">
                <iframe
                  title={t("contact.mapTitle")}
                  src="https://www.google.com/maps?q=4B+Avenue+Durante,+06000+Nice,+France&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0 w-full h-full border-0"
                  allowFullScreen
                />
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="bg-secondary border border-border/60 p-8 md:p-12">
              <ContactForm defaultInterest={defaultInterest} />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
